/**
 * @file Assignment.jsx
 * @description Assignment component for displaying and managing assignment table.
 * @component Assignment
 * @exports Assignment - Main component for displaying and managing assignment table.
 */

import { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import Stack from '@mui/material/Stack';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { TextField, Button, Alert } from "@mui/material";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getIvrAssignment, completeIvrAssignment, closeIvrAssignment } from "../../../services/ivrModule/assignmentServices.js";
import { AUTO_COMPLETE, VARIANTS, BUTTON_COLORS, STATUS, SORT_ORDERS, SIZES } from "../../../common/constants/commonConstants.js";
import { ASSIGNMENT_STRINGS, ASSIGNMENT_KEYS, ASSIGNMENT_MESSAGES, ASSIGNMENT_ERRORS, ASSIGNMENT_TABLE_COLUMNS, ASSIGNMENT_ACTIONS, ASSIGNMENT_STATUS } from "../../../common/constants/ivrModule/assignmentConstants.js";
import AssignmentTable from "./AssignmentTable.jsx";
import CloseCompleteAssignmentDialog from "./CloseCompleteAssignmentDialog.jsx";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";

const Assignment = () => {
    const [page, setPage] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedIds, setSelectedIds] = useState([]);
    const [popupAction, setPopupAction] = useState(null);
    const [popupMessage, setPopupMessage] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [popupLoading, setPopupLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [ivrAssignments, setIvrAssignments] = useState([]);
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [validAssignments, setValidAssignments] = useState([]);
    const [loadingAssignment, setLoadingAssignment] = useState(true);
    const [searchIvrAssignment, setSearchIvrAssignment] = useState("");
    const [orderBy, setOrderBy] = useState(ASSIGNMENT_KEYS.ASSIGNMENT_NAME);

    useEffect(() => { fetchAssignment(); }, []);

    useEffect(() => {
        setSelectedIds([]);
    }, [rowsPerPage]);

    useEffect(() => {
        setPage(0);
    }, [searchIvrAssignment, statusFilter]);

    const fetchAssignment = async () => {
        try {
            setLoadingAssignment(true);
            setIvrAssignments(await getIvrAssignment());
        }
        catch (error) {
            console.error(ASSIGNMENT_ERRORS.FETCH_ASSIGNMENT_ERROR, error);
        }
        finally {
            setLoadingAssignment(false);
        }
    };

    const statusOptions = useMemo(() => {
        const allStatuses = ivrAssignments.map(ivrAssignment => ivrAssignment.status).filter(Boolean);
        return Array.from(new Set(allStatuses));
    }, [ivrAssignments]);

    const { assignmentsToDisplay, filteredAssignmentLength } = useMemo(() => {
        let filteredAssignment = ivrAssignments.filter((ivrAssignment) =>
            ivrAssignment.id.trim().toLowerCase().includes(searchIvrAssignment.toLowerCase())
        );
        if (statusFilter) {
            filteredAssignment = filteredAssignment.filter((assignment) => assignment.status === statusFilter);
        }
        const sortedAssignments = filteredAssignment.sort(getComparator(order, orderBy));
        return {
            assignmentsToDisplay: sortedAssignments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredAssignmentLength: filteredAssignment.length,
        };
    }, [ivrAssignments, searchIvrAssignment, statusFilter, order, orderBy, page, rowsPerPage]);

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    const handleClose = async () => {
        setPopupAction(ASSIGNMENT_ACTIONS.CLOSE);
        if (selectedIds.length === 0) return;
        const selectedAssignments = ivrAssignments.filter(assignment => selectedIds.includes(assignment.id));
        const assignmentsToClose = selectedAssignments.filter(assignment => assignment.status === ASSIGNMENT_STATUS.COMPLETED);
        if (assignmentsToClose.length > 0) {
            setPopupMessage(
                `${assignmentsToClose.length} ${ASSIGNMENT_STRINGS.OUT_OF} ${selectedAssignments.length} ${ASSIGNMENT_STRINGS.ASSIGNMENTS_SELECTED} ${ASSIGNMENT_STRINGS.BE_CLOSED}`
            );
            setValidAssignments(assignmentsToClose);
        }
        else {
            setPopupMessage(`${ASSIGNMENT_STRINGS.NONE_ASSIGNMENTS_SELECTED} ${ASSIGNMENT_STRINGS.CLOSABLE_STATUS}`);
        }
        setPopupOpen(true);
    };

    const handleComplete = async () => {
        setPopupAction(ASSIGNMENT_ACTIONS.COMPLETE);
        if (selectedIds.length === 0) return;
        const selectedAssignments = ivrAssignments.filter(assignment => selectedIds.includes(assignment.id));
        const assignmentsToComplete = selectedAssignments.filter(assignment => assignment.status === ASSIGNMENT_STATUS.ASSIGNED);
        if (assignmentsToComplete.length > 0) {
            setPopupMessage(
                `${assignmentsToComplete.length} ${ASSIGNMENT_STRINGS.OUT_OF} ${selectedAssignments.length} ${ASSIGNMENT_STRINGS.ASSIGNMENTS_SELECTED} ${ASSIGNMENT_STRINGS.BE_COMPLETED}`
            );
            setValidAssignments(assignmentsToComplete);
        }
        else {
            setPopupMessage(`${ASSIGNMENT_STRINGS.NONE_ASSIGNMENTS_SELECTED} ${ASSIGNMENT_STRINGS.COMPLETABLE_STATUS}`);
        }
        setPopupOpen(true);
    };

    const confirmPopupAction = async () => {
        setPopupOpen(false);
        setPopupLoading(true); 
        try {
            if (popupAction === ASSIGNMENT_ACTIONS.CLOSE) {
                await Promise.all(validAssignments.map(assignment => closeIvrAssignment({
                    assignmentId: assignment.id,
                    workerId: assignment.workerId,
                    jobId: assignment.jobid,
                })));
                setSuccessMessage(ASSIGNMENT_MESSAGES.ASSIGNMENT_CLOSED);
            } else if (popupAction === ASSIGNMENT_ACTIONS.COMPLETE) {
                await Promise.all(validAssignments.map(assignment => completeIvrAssignment({
                    assignmentId: assignment.id,
                    workerId: assignment.workerId,
                    jobId: assignment.jobid,
                })));
                setSuccessMessage(ASSIGNMENT_MESSAGES.ASSIGNMENT_COMPLETED);
            }
            setSelectedIds([]);
            await fetchAssignment();
        } catch (error) {
            console.error(`${popupAction} failed:`, error);
            setSuccessMessage(`Failed to ${popupAction} assignment(s). Please try again.`);
        } finally {
            setPopupLoading(false);
            setPopupAction(null);
            setValidAssignments([]);
            setTimeout(() => setSuccessMessage(""), 3000);
        }
    };

    return (
        <>
            {popupLoading && (
				<>
					<ProgressOverlay />
				</>
			)}
            {successMessage &&
                <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                    {successMessage}
                </Alert>
            }
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={SIZES.GROW}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        value={searchIvrAssignment}
                        variant={VARIANTS.OUTLINED}
                        label={ASSIGNMENT_STRINGS.SEARCH_ASSIGNMENT}
                        autoComplete={AUTO_COMPLETE.OFF}
                        onChange={e => setSearchIvrAssignment(e.target.value)}
                    />
                </Grid>
                <Grid size={SIZES.AUTO}>
                    <Stack spacing={2} direction="row">
                        <Button
                            onClick={handleClose}
                            startIcon={<CloseIcon />}
                            variant={VARIANTS.CONTAINED}
                            disabled={selectedIds.length === 0}
                            color={selectedIds.length === 0 ? BUTTON_COLORS.SECONDARY : BUTTON_COLORS.ERROR}
                        >
                            {ASSIGNMENT_STRINGS.CLOSE_ASSIGNMENT}
                        </Button>
                        <Button
                            onClick={handleComplete}
                            startIcon={<DoneIcon />}
                            variant={VARIANTS.CONTAINED}
                            disabled={selectedIds.length === 0}
                            color={selectedIds.length === 0 ? BUTTON_COLORS.SECONDARY : BUTTON_COLORS.SUCCESS}
                        >
                            {ASSIGNMENT_STRINGS.COMPLETE_ASSIGNMENT}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
            <AssignmentTable
                page={page}
                order={order}
                setPage={setPage}
                orderBy={orderBy}
                rowsPerPage={rowsPerPage}
                selectedIds={selectedIds}
                statusFilter={statusFilter}
                statusOptions={statusOptions}
                setRowsPerPage={setRowsPerPage}
                columns={ASSIGNMENT_TABLE_COLUMNS}
                assignments={assignmentsToDisplay}
                onSelectionChange={setSelectedIds}
                handleRequestSort={handleRequestSort}
                onStatusFilterChange={setStatusFilter}
                isAssignmentLoading={loadingAssignment}
                filteredAssignmentLength={filteredAssignmentLength}
            />
            <CloseCompleteAssignmentDialog
                open={popupOpen}
                action={popupAction}
                message={popupMessage}
                validAssignments={validAssignments.length}
                onConfirm={confirmPopupAction}
                onClose={() => setPopupOpen(false)}
            />
        </>
    );
};

export default Assignment;